import { Currency } from '../enum/currency'
import { SignInMethod } from '../enum/sign-in-method'
import { Weekdays } from '../enum/weekdays'
import { Factory } from '../interface/factory'
import { AvailablePayload } from './available-payload'
import { ExpiryReminder, ExpiryReminderType } from './expiry-reminder'
import { GracePeriod } from './grace-period'
import {
  AgnosticAdyenServiceProvider,
  PaymentMethodType,
  SupportedPaymentMethod,
} from './payment-method'
import { LengthPriceCap, Price } from './price-cap'
import { Rate } from './rate'
import { Setting } from './setting'
import { UnlockPrice, UnlockPriceType } from './unlock-price'
import { VisualProfile } from './visual-profile'
import { PriceSlots } from './price-slots'
import {
  SessionPriceAlgorithm,
  SessionPriceAlgorithmType,
} from './session-price-algorithm'
import {
  SharedAccess,
  SharedAccessBasePrice,
  SharedAccessBeneficiaryPrice,
  collectSharedAccessExcludedCategoryIds,
} from './shared-access'
import { RegularRateAfterExpiry } from './regular-rate-after-expiry'
import { parseSlotRefundModeType, UnusedTimeRefund } from './unused-time-refund'
import { isStrictlyPositivePriceAmount } from '../../_utils/strictly-positive-price-amount.util'
import { settingKeysForDirtyControlPaths } from '../../_utils/settings-form-dirty-keys.util'
import {
  attachRaw,
  attachUnknown,
  dirtyPatch,
  editsFromView,
  getRaw,
  getUnknown,
  hasRaw,
  isDegraded,
  mergePatch,
  parseEntity,
  rememberPayload,
  toWire,
  unwrapLegacyExtras,
  wireEntity,
} from '../../_utils/fcdc-v2'
import { SETTINGS_KEYS, SettingsSchema } from '../schema/settings.schema'
import { FormGroup } from '@angular/forms'

export interface Duration {
  secs: number
  nanos: number
}

export class Settings {
  acceptedCurrencies?: Setting<Currency[]>
  defaultCurrency?: Setting<Currency>
  vat?: Setting<number>
  termsAndConditionsUrl?: Setting<string>
  privacyPolicyUrl?: Setting<string>
  visualProfile?: Setting<VisualProfile>
  sessionAllowedSignInMethods?: Setting<SignInMethod[]>
  sessionMaxLength?: Setting<Duration>
  sessionFreePaymentThreshold?: Setting<Price[]>
  gracePeriod?: Setting<GracePeriod>
  refundGracePeriod?: Setting<Duration>
  expiryReminder?: Setting<ExpiryReminder>
  lockerBaseRates?: Setting<Rate[]>
  sessionLengthPriceCap?: Setting<LengthPriceCap>
  sessionOverdueLengthPriceCap?: Setting<LengthPriceCap>
  unlockPrices?: Setting<UnlockPrice[]>
  serviceFee?: Setting<Price[]>
  depositionPrice?: Setting<Price[]>
  availableHours?: Setting<Rule<AvailablePayload>[]>
  supportedPaymentMethods?: Setting<SupportedPaymentMethod[]>
  lockerBookingAllowed?: Setting<boolean>
  lockerBookingLeadTime?: Setting<Duration>
  sessionPriceAlgorithm?: Setting<SessionPriceAlgorithm>
  sessionPriceSlots?: Setting<PriceSlots>
  sessionSharedAccess?: Setting<SharedAccess>
  unusedTimeRefund?: Setting<UnusedTimeRefund>
  regularRateAfterExpiry?: Setting<RegularRateAfterExpiry>

  constructor(json: any) {
    const source = unwrapLegacyExtras(
      json && typeof json === 'object' ? json : {}
    ) as any
    this.acceptedCurrencies = source.acceptedCurrencies
      ? new Setting<Currency[]>(source.acceptedCurrencies)
      : undefined
    this.defaultCurrency = source.defaultCurrency
      ? new Setting<Currency>(source.defaultCurrency)
      : undefined
    this.vat = source.vat ? new Setting<number>(source.vat) : undefined
    this.termsAndConditionsUrl = source.termsAndConditionsUrl
      ? new Setting<string>(source.termsAndConditionsUrl)
      : undefined
    this.privacyPolicyUrl = source.privacyPolicyUrl
      ? new Setting<string>(source.privacyPolicyUrl)
      : undefined
    this.visualProfile = source.visualProfile
      ? new Setting<VisualProfile>(source.visualProfile)
      : undefined
    this.availableHours = source.availableHours
      ? new Setting<Rule<AvailablePayload>[]>(source.availableHours)
      : undefined
    this.sessionAllowedSignInMethods = source.sessionAllowedSignInMethods
      ? new Setting<SignInMethod[]>(source.sessionAllowedSignInMethods)
      : undefined
    this.sessionMaxLength = source.sessionMaxLength
      ? new Setting<Duration>(source.sessionMaxLength)
      : undefined
    this.sessionFreePaymentThreshold = source.sessionFreePaymentThreshold
      ? new Setting<Price[]>(source.sessionFreePaymentThreshold)
      : undefined
    this.lockerBaseRates = source.lockerBaseRates
      ? new Setting<Rate[]>(source.lockerBaseRates)
      : undefined
    this.sessionLengthPriceCap = source.sessionLengthPriceCap
      ? new Setting<LengthPriceCap>(source.sessionLengthPriceCap)
      : undefined

    this.sessionOverdueLengthPriceCap = source.sessionOverdueLengthPriceCap
      ? new Setting<LengthPriceCap>(source.sessionOverdueLengthPriceCap)
      : undefined

    this.unlockPrices = source.unlockPrices
      ? new Setting<UnlockPrice[]>(source.unlockPrices)
      : undefined
    this.serviceFee = source.serviceFee
      ? new Setting<Price[]>(source.serviceFee)
      : undefined
    this.depositionPrice = source.depositionPrice
      ? new Setting<Price[]>(source.depositionPrice)
      : undefined

    this.gracePeriod = source.gracePeriod
      ? new Setting<GracePeriod>(source.gracePeriod)
      : undefined
    this.refundGracePeriod = source.refundGracePeriod
      ? new Setting<Duration>(source.refundGracePeriod)
      : undefined
    this.expiryReminder = source.expiryReminder
      ? new Setting<ExpiryReminder>(source.expiryReminder)
      : undefined
    this.supportedPaymentMethods = source.supportedPaymentMethods
      ? new Setting<SupportedPaymentMethod[]>(source.supportedPaymentMethods)
      : undefined
    this.lockerBookingAllowed = source.lockerBookingAllowed
      ? new Setting<boolean>(source.lockerBookingAllowed)
      : undefined
    this.lockerBookingLeadTime = source.lockerBookingLeadTime
      ? new Setting<Duration>(source.lockerBookingLeadTime)
      : undefined
    this.sessionPriceAlgorithm = source.sessionPriceAlgorithm
      ? new Setting<SessionPriceAlgorithm>(source.sessionPriceAlgorithm)
      : undefined
    this.sessionPriceSlots = source.sessionPriceSlots
      ? new Setting<PriceSlots>(source.sessionPriceSlots)
      : undefined
    this.sessionSharedAccess = source.sessionSharedAccess
      ? new Setting<SharedAccess>(source.sessionSharedAccess)
      : undefined
    this.unusedTimeRefund = source.unusedTimeRefund
      ? new Setting<UnusedTimeRefund>(source.unusedTimeRefund)
      : undefined
    this.regularRateAfterExpiry = source.regularRateAfterExpiry
      ? new Setting<RegularRateAfterExpiry>(source.regularRateAfterExpiry)
      : undefined
    this.applySchema(source)
  }

  private applySchema(source: unknown): void {
    const parsed = parseEntity(SettingsSchema, source)
    const unknown = getUnknown(parsed)
    const record = this as unknown as Record<string, unknown>
    for (const key of SETTINGS_KEYS) {
      const mismatched = unknown.some(
        path => path === key || path.startsWith(`${key}.`)
      )
      if (mismatched) {
        record[key] = undefined
      }
    }
    attachRaw(this, source)
    attachUnknown(this, unknown, isDegraded(parsed))
  }

  toJSON(): Record<string, unknown> {
    return wireEntity(this) as Record<string, unknown>
  }

  static getFactory(): Factory<Settings> {
    return new (class implements Factory<Settings> {
      make(json: any): Settings {
        return new Settings(json)
      }

      getTableName(): string {
        throw new Error(
          'getTableName() should never be called on a Settings. Settings are not stored in IndexDB.'
        )
      }
    })()
  }

  static formatDate(date: Date) {
    return date ? new Date(date).toISOString().split('T')[0] : null
  }

  static formatTime(hour: string, minutes: string): string | null {
    const parsedHour = parseInt(hour, 10)
    const parsedMinute = parseInt(minutes, 10)

    if (
      isNaN(parsedHour) ||
      isNaN(parsedMinute) ||
      parsedHour < 0 ||
      parsedHour > 23 ||
      parsedMinute < 0 ||
      parsedMinute > 59
    ) {
      console.error('No available hours set')
      return null
    }

    const date = new Date()

    date.setUTCHours(parsedHour)
    date.setUTCMinutes(parsedMinute)
    date.setUTCSeconds(0)

    return date.toISOString().split('T')[1].split('.')[0]
  }

  static createDuration(totalSeconds: number): Duration {
    const secs = Math.floor(totalSeconds * 60)
    const nanos = 0
    return { secs, nanos }
  }

  static createSettingsFromForm(
    form: any,
    isCountry: boolean = false,
    allowedCurrenciesFromParent: Currency[] = [Currency.EUR],
    defaultCurrencyFromParent: Currency | null = null,
    _source?: Settings | null
  ): Settings | undefined {
    const settings = new Settings({})

    settings.acceptedCurrencies =
      form && form.acceptedCurrencies
        ? new Setting<Currency[]>({
            actual: form.acceptedCurrencies,
            notOverridable: !!form.acceptedCurrenciesNotOverridable,
            countryOverrideIllegal:
              isCountry && !!form.acceptedCurrenciesNotOverridable,
          })
        : new Setting<Currency[]>({
            actual: allowedCurrenciesFromParent,
            notOverridable: false,
            countryOverrideIllegal: false,
          })

    settings.defaultCurrency =
      form && form.defaultCurrency
        ? new Setting<Currency>({
            actual: form.defaultCurrency,
            notOverridable: !!form.defaultCurrencyNotOverridable,
            countryOverrideIllegal:
              isCountry && !!form.defaultCurrencyNotOverridable,
          })
        : new Setting<Currency>({
            actual: defaultCurrencyFromParent ?? allowedCurrenciesFromParent[0],
            notOverridable: false,
            countryOverrideIllegal: false,
          })

    if (!form) {
      return settings
    }

    settings.sessionAllowedSignInMethods = new Setting<SignInMethod[]>({
      actual: [SignInMethod.SpeedboxEmail, SignInMethod.SpeedboxPhone],
      notOverridable: !!form.sessionAllowedSignInMethodsNotOverridable,
      countryOverrideIllegal:
        isCountry && !!form.sessionAllowedSignInMethodsNotOverridable,
    })

    if (isCountry) {
      settings.vat = new Setting<number>({
        actual: form.vat ? parseFloat((form.vat / 100).toFixed(6)) : undefined,
        notOverridable: isCountry ? !!form.vatNotOverridable : true,
        countryOverrideIllegal: true,
      })
    } else {
      settings.vat = undefined
    }

    if (form.termsAndConditionsUrl || form.termsAndConditionsUrl === '') {
      settings.termsAndConditionsUrl = new Setting<string>({
        actual: form.termsAndConditionsUrl || null,
        notOverridable: !!form.termsAndConditionsUrlNotOverridable,
        countryOverrideIllegal:
          isCountry && !!form.termsAndConditionsUrlNotOverridable,
      })
    }

    if (form.privacyPolicyUrl) {
      settings.privacyPolicyUrl = new Setting<string>({
        actual: form.privacyPolicyUrl,
        notOverridable: !!form.privacyPolicyUrlNotOverridable,
        countryOverrideIllegal:
          isCountry && !!form.privacyPolicyUrlNotOverridable,
      })
    }

    if (form.visualProfile && form.visualProfile.logoUrl) {
      settings.visualProfile = new Setting<VisualProfile>({
        actual: form.visualProfile,
        notOverridable: !!form.visualProfileNotOverridable,
        countryOverrideIllegal: isCountry && !!form.visualProfileNotOverridable,
      })
    }

    if (
      form.customerMerchantAccount &&
      form.customerClientKey &&
      form.supportedPaymentMethods
    ) {
      settings.supportedPaymentMethods = new Setting<SupportedPaymentMethod[]>({
        actual: form.supportedPaymentMethods.map(
          (pm: PaymentMethodType | any) => {
            const factory = SupportedPaymentMethod.getFactory()

            let paymentMethodType: PaymentMethodType
            let providers: AgnosticAdyenServiceProvider[]

            if (typeof pm === 'string') {
              paymentMethodType = pm as PaymentMethodType
              providers = [
                new AgnosticAdyenServiceProvider({
                  merchantAccount: form.customerMerchantAccount,
                  clientKey: form.customerClientKey,
                }),
              ]
            } else if (pm && typeof pm === 'object' && pm.type) {
              paymentMethodType = pm.type as PaymentMethodType
              if (
                pm.providers &&
                Array.isArray(pm.providers) &&
                pm.providers.length > 0
              ) {
                providers = pm.providers.map(
                  (p: any) => new AgnosticAdyenServiceProvider(p)
                )
              } else {
                providers = [
                  new AgnosticAdyenServiceProvider({
                    merchantAccount: form.customerMerchantAccount,
                    clientKey: form.customerClientKey,
                  }),
                ]
              }
            } else {
              paymentMethodType = pm as PaymentMethodType
              providers = [
                new AgnosticAdyenServiceProvider({
                  merchantAccount: form.customerMerchantAccount,
                  clientKey: form.customerClientKey,
                }),
              ]
            }

            return factory.make({
              type: paymentMethodType,
              providers: providers,
            })
          }
        ),
        notOverridable: !!form.supportedPaymentMethodsNotOverridable,
        countryOverrideIllegal:
          isCountry && !!form.supportedPaymentMethodsNotOverridable,
      })
    }

    if (form.sessionMaxLength) {
      settings.sessionMaxLength = new Setting<Duration>({
        actual: Settings.createDuration(parseInt(form.sessionMaxLength)),
        notOverridable: !!form.sessionMaxLengthNotOverridable,
        countryOverrideIllegal:
          isCountry && !!form.sessionMaxLengthNotOverridable,
      })
    }

    if (
      isCountry &&
      form.sessionFreePaymentThreshold &&
      form.sessionFreePaymentThreshold.every(
        (t: Price) =>
          t.currency && isStrictlyPositivePriceAmount(t.amount)
      )
    ) {
      settings.sessionFreePaymentThreshold = new Setting<Price[]>({
        actual: form.sessionFreePaymentThreshold.map((s: Price) => ({
          amount: s.amount,
          currency: s.currency,
        })),
        notOverridable: !!form.sessionFreePaymentThresholdNotOverridable,
        countryOverrideIllegal:
          isCountry && !!form.sessionFreePaymentThresholdNotOverridable,
      })
    }

    if (form.gracePeriodDuration && form.gracePeriodMaxUnlocks) {
      settings.gracePeriod = new Setting<GracePeriod>({
        actual: {
          duration: Settings.createDuration(parseInt(form.gracePeriodDuration)),
          maxUnlocks: parseInt(form.gracePeriodMaxUnlocks),
        },
        notOverridable: !!form.gracePeriodNotOverridable,
        countryOverrideIllegal: isCountry && !!form.gracePeriodNotOverridable,
      })
    }

    if (form.lockerBookingLeadTime) {
      settings.lockerBookingLeadTime = new Setting<Duration>({
        actual: Settings.createDuration(parseInt(form.lockerBookingLeadTime)),
        notOverridable: !!form.lockerBookingLeadTimeNotOverridable,
        countryOverrideIllegal:
          isCountry && !!form.lockerBookingLeadTimeNotOverridable,
      })
    }

    if (isCountry) {
      settings.lockerBookingAllowed = new Setting<boolean>({
        actual: true,
        notOverridable: true,
        countryOverrideIllegal: true,
      })
    }

    if (form.expiryReminder === false) {
      settings.expiryReminder = new Setting<ExpiryReminder>({
        actual: null,
        notOverridable: !!form.expiryReminderNotOverridable,
        countryOverrideIllegal:
          isCountry && !!form.expiryReminderNotOverridable,
      })
    } else {
      settings.expiryReminder =
        form.expiryReminder === undefined
          ? undefined
          : form.expiryReminderType &&
              form.expiryReminderType === ExpiryReminderType.Paid
            ? form.durationUntilExpiry &&
              form.expiryReminderPrices.every(
                (t: Price) => t.currency && t.amount
              )
              ? new Setting<ExpiryReminder>({
                  actual: {
                    type: form.expiryReminderType,
                    durationUntilExpiry: this.createDuration(
                      parseInt(form.durationUntilExpiry)
                    ),
                    prices: form.expiryReminderPrices.map((price: Price) => ({
                      amount: price.amount,
                      currency: price.currency,
                    })),
                  },
                  notOverridable: !!form.expiryReminderNotOverridable,
                  countryOverrideIllegal:
                    isCountry && !!form.expiryReminderNotOverridable,
                })
              : undefined
            : form.durationUntilExpiry
              ? new Setting<ExpiryReminder>({
                  actual: {
                    type: form.expiryReminderType,
                    durationUntilExpiry: this.createDuration(
                      parseInt(form.durationUntilExpiry)
                    ),
                    prices: [],
                  },
                  notOverridable: !!form.expiryReminderNotOverridable,
                  countryOverrideIllegal:
                    isCountry && !!form.expiryReminderNotOverridable,
                })
              : undefined
    }

    if (form.bookingRates && form.bookingRates.length > 0) {
      const transformedRates: Rate[] = []
      const sessionLengthBlocks: any[] = []
      const sessionOverdueBlocks: any[] = []

      form.bookingRates.forEach((category: any) => {
        category.prices.forEach((price: any) => {
          if (price.perHour !== '' && price.penaltyPerHour !== '') {
            transformedRates.push({
              priceCategoryName: category.categoryLabel,
              priceCategoryId: category.categoryId,
              currency: price.currency,
              pricePerAllocatedHour: price.perHour,
              pricePerUnallocatedHour: price.penaltyPerHour,
              pricePerRollingHour: '0',
            } as Rate)
          }

          if (form.sessionLengthPriceCapEnabled && price.dailyCap !== '') {
            sessionLengthBlocks.push({
              priceCategoryId: category.categoryId,
              priceCap: {
                amount: price.dailyCap,
                currency: price.currency,
              },
              length: { secs: 86400, nanos: 0 },
              repeating: true,
            })
          }

          if (
            form.sessionOverdueLengthPriceCapEnabled &&
            price.overdueCap !== ''
          ) {
            sessionOverdueBlocks.push({
              priceCategoryId: category.categoryId,
              priceCap: {
                amount: price.overdueCap,
                currency: price.currency,
              },
              length: { secs: 86400, nanos: 0 },
              repeating: true,
            })
          }
        })
      })

      const allRatesFilled = transformedRates.every(
        rate =>
          rate.pricePerAllocatedHour !== '' &&
          rate.pricePerUnallocatedHour !== ''
      )
      if (allRatesFilled && transformedRates.length > 0) {
        settings.lockerBaseRates = new Setting<Rate[]>({
          actual: transformedRates,
          notOverridable: Boolean(form.lockerBaseRatesNotOverridable),
          countryOverrideIllegal: Boolean(
            isCountry && form.lockerBaseRatesNotOverridable
          ),
        })
      }

      if (form.sessionLengthPriceCapEnabled === false) {
        settings.sessionLengthPriceCap = new Setting<LengthPriceCap>({
          actual: null,
          notOverridable: Boolean(form.sessionLengthPriceCapNotOverridable),
          countryOverrideIllegal: Boolean(
            isCountry && form.sessionLengthPriceCapNotOverridable
          ),
        })
      } else if (sessionLengthBlocks.length > 0) {
        settings.sessionLengthPriceCap = new Setting<LengthPriceCap>({
          actual: { blocks: sessionLengthBlocks },
          notOverridable: Boolean(form.sessionLengthPriceCapNotOverridable),
          countryOverrideIllegal: Boolean(
            isCountry && form.sessionLengthPriceCapNotOverridable
          ),
        })
      }

      if (form.sessionOverdueLengthPriceCapEnabled === false) {
        settings.sessionOverdueLengthPriceCap = new Setting<LengthPriceCap>({
          actual: null,
          notOverridable: Boolean(
            form.sessionOverdueLengthPriceCapNotOverridable
          ),
          countryOverrideIllegal: Boolean(
            isCountry && form.sessionOverdueLengthPriceCapNotOverridable
          ),
        })
      } else if (sessionOverdueBlocks.length > 0) {
        settings.sessionOverdueLengthPriceCap = new Setting<LengthPriceCap>({
          actual: { blocks: sessionOverdueBlocks },
          notOverridable: Boolean(
            form.sessionOverdueLengthPriceCapNotOverridable
          ),
          countryOverrideIllegal: Boolean(
            isCountry && form.sessionOverdueLengthPriceCapNotOverridable
          ),
        })
      }
    }

    if (form.unlockPriceEnabled === false) {
      settings.unlockPrices = new Setting<UnlockPrice[]>({
        actual: null,
        notOverridable: Boolean(form.unlockPriceNotOverridable),
        countryOverrideIllegal: Boolean(
          isCountry && form.unlockPriceNotOverridable
        ),
      })
    } else if (
      form.unlockPrice?.every((t: any) => t.price.amount && t.price.currency)
    ) {
      settings.unlockPrices = new Setting<UnlockPrice[]>({
        actual: form.unlockPrice.map((item: any) => {
          if (item.unlockPriceType === UnlockPriceType.Limited) {
            const parsedCount = parseInt(item.count, 10)
            return {
              count:
                Number.isFinite(parsedCount) && parsedCount >= 1
                  ? parsedCount
                  : 1,
              price: {
                currency: item.price.currency,
                amount: item.price.amount,
              },
              type: item.unlockPriceType,
            }
          }

          return {
            price: {
              currency: item.price.currency,
              amount: item.price.amount,
            },
            type: item.unlockPriceType,
          }
        }),
        notOverridable: Boolean(form.unlockPriceNotOverridable),
        countryOverrideIllegal: Boolean(
          isCountry && form.unlockPriceNotOverridable
        ),
      })
    }

    if (form.serviceFeeEnabled === false) {
      settings.serviceFee = new Setting<Price[]>({
        actual: null,
        notOverridable: Boolean(form.serviceFeeNotOverridable),
        countryOverrideIllegal: Boolean(
          isCountry && form.serviceFeeNotOverridable
        ),
      })
    } else if (form.serviceFee?.every((t: any) => t.amount && t.currency)) {
      settings.serviceFee = new Setting<Price[]>({
        actual: form.serviceFee.map((item: Price) => ({
          currency: item.currency,
          amount: item.amount,
        })),
        notOverridable: Boolean(form.serviceFeeNotOverridable),
        countryOverrideIllegal: Boolean(
          isCountry && form.serviceFeeNotOverridable
        ),
      })
    }

    if (form.depositionPriceEnabled === false) {
      settings.depositionPrice = new Setting<Price[]>({
        actual: null,
        notOverridable: Boolean(form.depositionPriceNotOverridable),
        countryOverrideIllegal: Boolean(
          isCountry && form.depositionPriceNotOverridable
        ),
      })
    } else if (
      form.depositionPrice?.every(
        (t: any) => t.depositionAmount && t.depositionCurrency
      )
    ) {
      settings.depositionPrice = new Setting<Price[]>({
        actual: form.depositionPrice.map((item: any) => ({
          amount: item.depositionAmount,
          currency: item.depositionCurrency,
        })),
        notOverridable: Boolean(form.depositionPriceNotOverridable),
        countryOverrideIllegal: Boolean(
          isCountry && form.depositionPriceNotOverridable
        ),
      })
    } else {
      settings.depositionPrice = undefined
    }

    if (
      form.sessionPriceAlgorithmType === SessionPriceAlgorithmType.Rates &&
      settings.lockerBaseRates
    ) {
      settings.sessionPriceAlgorithm = new Setting<SessionPriceAlgorithm>({
        actual: SessionPriceAlgorithm.getFactory().make({
          type: SessionPriceAlgorithmType.Rates,
        }),
        notOverridable: Boolean(form.sessionPriceAlgorithmNotOverridable),
        countryOverrideIllegal: Boolean(
          isCountry && form.sessionPriceAlgorithmNotOverridable
        ),
      })
    }

    if (
      form.sessionPriceAlgorithmType === SessionPriceAlgorithmType.Slots &&
      form.sessionPriceSlots
    ) {
      const booking = form.sessionPriceSlots.bookingSlots || []
      const flattenedSlots: any[] = []
      const flattenedOverflow: any[] = []

      booking.forEach((cat: any) => {
        const categoryId = cat.categoryId

        ;(cat.slots || []).forEach((s: any) => {
          const unit = Number(s.durationUnit || 60)
          const value = s.duration == null ? 0 : Number(s.duration)
          const secs = value ? value * unit : 0

          const tierRows: {
            priceCategoryId: string
            length: { secs: number; nanos: number }
            price: { currency: Currency | undefined; amount: any }
          }[] = []
          ;(s.currencies || []).forEach((c: any) => {
            const raw = c?.amount
            const hasAmount =
              raw !== null &&
              raw !== undefined &&
              !(typeof raw === 'string' && raw.trim() === '')
            if (!hasAmount) {
              return
            }
            tierRows.push({
              priceCategoryId: categoryId,
              length: { secs, nanos: 0 },
              price: { currency: c.currency, amount: c.amount },
            })
          })
          if (tierRows.length === 0) {
            tierRows.push({
              priceCategoryId: categoryId,
              length: { secs, nanos: 0 },
              price: { currency: undefined, amount: undefined },
            })
          }
          flattenedSlots.push(...tierRows)
        })

        const o = cat.overflow
        if (o) {
          const unit = Number(o.durationUnit || 60)
          const value = o.duration == null ? 0 : Number(o.duration)
          const secs = value ? value * unit : 0
          const overflowRows: {
            priceCategoryId: string
            length: { secs: number; nanos: number }
            price: { currency: Currency | undefined; amount: any }
          }[] = []
          ;(o.currencies || []).forEach((c: any) => {
            const raw = c?.amount
            const hasAmount =
              raw !== null &&
              raw !== undefined &&
              !(typeof raw === 'string' && raw.trim() === '')
            if (!hasAmount) {
              return
            }
            overflowRows.push({
              priceCategoryId: categoryId,
              length: { secs, nanos: 0 },
              price: { currency: c.currency, amount: c.amount },
            })
          })
          if (overflowRows.length === 0) {
            overflowRows.push({
              priceCategoryId: categoryId,
              length: { secs, nanos: 0 },
              price: { currency: undefined, amount: undefined },
            })
          }
          flattenedOverflow.push(...overflowRows)
        }
      })

      const sessionPriceSlotRowHasPrice = (row: {
        price: { currency?: Currency; amount?: unknown }
      }) => {
        const { currency, amount } = row.price || {}
        return (
          currency != null && amount != null && String(amount).trim() !== ''
        )
      }

      const hasPricedSessionSlots =
        flattenedSlots.some(sessionPriceSlotRowHasPrice) ||
        flattenedOverflow.some(sessionPriceSlotRowHasPrice)

      if (hasPricedSessionSlots) {
        settings.sessionPriceAlgorithm = new Setting<SessionPriceAlgorithm>({
          actual: SessionPriceAlgorithm.getFactory().make({
            type: SessionPriceAlgorithmType.Slots,
          }),
          notOverridable: Boolean(form.sessionPriceAlgorithmNotOverridable),
          countryOverrideIllegal: Boolean(
            isCountry && form.sessionPriceAlgorithmNotOverridable
          ),
        })

        settings.sessionPriceSlots = new Setting<PriceSlots>({
          actual: new PriceSlots({
            slots: flattenedSlots,
            overflowSlot: flattenedOverflow,
          }),
          notOverridable: Boolean(form.sessionPriceSlotsNotOverridable),
          countryOverrideIllegal: Boolean(
            isCountry && form.sessionPriceSlotsNotOverridable
          ),
        })
      }
    }

    if (
      form.sessionPriceAlgorithmType === SessionPriceAlgorithmType.Slots ||
      form.sessionPriceAlgorithmType === SessionPriceAlgorithmType.Rates
    ) {
      settings.unusedTimeRefund = new Setting<UnusedTimeRefund>({
        actual: {
          enabled: Boolean(form.unusedTimeRefundEnabled),
          slotRefundMode: {
            type: parseSlotRefundModeType(form.unusedTimeRefundSlotRefundMode),
          },
        },
        notOverridable: Boolean(form.unusedTimeRefundNotOverridable),
        countryOverrideIllegal: Boolean(
          isCountry && form.unusedTimeRefundNotOverridable
        ),
      })
    }

    if (form.sessionPriceAlgorithmType === SessionPriceAlgorithmType.Rates) {
      settings.regularRateAfterExpiry = new Setting<RegularRateAfterExpiry>({
        actual: {
          enabled: Boolean(form.regularRateAfterExpiryEnabled),
        },
        notOverridable: Boolean(form.regularRateAfterExpiryNotOverridable),
        countryOverrideIllegal: Boolean(
          isCountry && form.regularRateAfterExpiryNotOverridable
        ),
      })
    }

    if (form.sharedAccessEnabled === false) {
      settings.sessionSharedAccess = new Setting<SharedAccess>({
        actual: null,
        notOverridable: Boolean(form.sharedAccessNotOverridable),
        countryOverrideIllegal: Boolean(
          isCountry && form.sharedAccessNotOverridable
        ),
      })
    } else if (form.sharedAccessEnabled === true) {
      const maxBen = form.sharedAccessMaxBeneficiaries
        ? parseInt(form.sharedAccessMaxBeneficiaries, 10)
        : undefined
      const maxBeneficiaries =
        maxBen !== undefined && !isNaN(maxBen) && maxBen > 0
          ? maxBen
          : undefined

      const excludedCategoryIds = collectSharedAccessExcludedCategoryIds(
        form.sharedAccessBasePrices || []
      )
      const excludedCategoryIdSet = new Set(excludedCategoryIds)

      const mapSharedAccessCategoryPrices = (
        cat: any
      ): SharedAccessBasePrice | null => {
        if (
          cat.excludedFromSharedAccess ||
          (cat.categoryId && excludedCategoryIdSet.has(cat.categoryId))
        ) {
          return null
        }
        const prices = (cat.prices || [])
          .filter((p: any) => p.amount !== '' && p.amount != null)
          .map((p: any) => ({ currency: p.currency, amount: p.amount }))
        if (prices.length === 0) {
          return null
        }
        return {
          priceCategoryId: cat.categoryId,
          prices,
        }
      }

      const basePrices: SharedAccessBasePrice[] = (
        form.sharedAccessBasePrices || []
      )
        .map(mapSharedAccessCategoryPrices)
        .filter(
          (bp: SharedAccessBasePrice | null): bp is SharedAccessBasePrice =>
            bp !== null
        )

      const beneficiaryPrices: SharedAccessBeneficiaryPrice[] = (
        form.sharedAccessBeneficiaryPrices || []
      )
        .map(mapSharedAccessCategoryPrices)
        .filter(
          (
            bp: SharedAccessBeneficiaryPrice | null
          ): bp is SharedAccessBeneficiaryPrice => bp !== null
        )

      const sharedAccessActual: SharedAccess = {
        maxBeneficiaries,
        basePrices,
        beneficiaryPrices,
        excludedCategoryIds,
      }

      // always persist when enabled so the API does not drop shared access
      // (e.g. new locker row with empty prices) and flip UI to "do not use".
      settings.sessionSharedAccess = new Setting<SharedAccess>({
        actual: sharedAccessActual,
        notOverridable: Boolean(form.sharedAccessNotOverridable),
        countryOverrideIllegal: Boolean(
          isCountry && form.sharedAccessNotOverridable
        ),
      })
    }

    settings.availableHours = new Setting<Rule<AvailablePayload>[]>({
      actual: [
        {
          startDate: '1970-01-01',
          endDate: '2999-12-30',
          startTime: '00:00:00',
          endTime: '00:00:00',
          weekdays: undefined,
          state: false,
          payload: undefined,
        },
        {
          startDate: '1970-01-01',
          endDate: '2999-12-30',
          startTime: '00:00:00',
          endTime: '00:00:00',
          weekdays: undefined,
          state: true,
          payload: undefined,
        },
      ],
      notOverridable: Boolean(form.availableHoursNotOverridable),
      countryOverrideIllegal: Boolean(
        isCountry && form.availableHoursNotOverridable
      ),
    })

    return settings
  }

  /**
   * Wire fragment for a settings save. Only fields touched by the form are included.
   * Array values are rebuilt from the source `$raw` items so unknown item fields survive.
   */
  /** Wire one setting wrapper for a parent settings patch without serializing the whole tree. */
  static wireSettingForSave(setting: Setting<unknown>): Record<string, unknown> {
    const wired = toWire(
      setting,
      editsFromView(setting, ['actual', 'notOverridable', 'countryOverrideIllegal'])
    ) as Record<string, unknown>
    if (setting.actual === null) {
      wired['actual'] = null
    }
    return wired
  }

  static settingsPatchFromForm(
    form: FormGroup | Record<string, unknown> | null | undefined,
    isCountry: boolean = false,
    allowedCurrenciesFromParent: Currency[] = [Currency.EUR],
    defaultCurrencyFromParent: Currency | null = null,
    source?: Settings | null
  ): Record<string, unknown> {
    const value =
      form instanceof FormGroup ? (form.getRawValue() as Record<string, unknown>) : (form ?? {})
    const dirtyNames =
      form instanceof FormGroup
        ? Object.keys(dirtyPatch(form))
        : Object.keys(value)
    const built = Settings.createSettingsFromForm(
      value,
      isCountry,
      allowedCurrenciesFromParent,
      defaultCurrencyFromParent
    )
    if (!built) {
      return {}
    }

    const keys = settingKeysForDirtyControlPaths(dirtyNames)
    if (value['sharedAccessEnabled'] === true) {
      keys.add('sessionSharedAccess')
    }
    const rawSettings = source && hasRaw(source) ? getRaw(source) : undefined
    const patch: Record<string, unknown> = {}
    const builtRecord = built as unknown as Record<string, Setting<unknown> | undefined>

    for (const key of keys) {
      const setting = builtRecord[key]
      if (!setting) {
        continue
      }
      const wired = wireEntity(setting) as Record<string, unknown>
      if (setting.actual === null) {
        wired['actual'] = null
      }
      patch[key] = overlaySettingForSave(key, wired, rawSettings)
    }

    return patch
  }

  /**
   * Pre-FCDC-v2 reset sent a sparse `settings` object on a full PUT, which dropped
   * every other key at this hierarchy level. Under merge-patch saves, replicate that by
   * nulling known keys on the stored settings, then writing the sparse form output
   * (currencies plus default sign-in methods from createSettingsFromForm).
   */
  static settingsResetPatchFromForm(
    form: Record<string, unknown>,
    isCountry: boolean = false,
    allowedCurrenciesFromParent: Currency[] = [Currency.EUR],
    defaultCurrencyFromParent: Currency | null = null,
    source?: Settings | null
  ): Record<string, unknown> {
    const built = Settings.createSettingsFromForm(
      form,
      isCountry,
      allowedCurrenciesFromParent,
      defaultCurrencyFromParent,
      source
    )
    if (!built) {
      return {}
    }

    const rawSettings =
      source && hasRaw(source)
        ? (getRaw(source) as Record<string, unknown>)
        : {}
    const builtRecord = built as unknown as Record<
      string,
      Setting<unknown> | undefined
    >
    const keysToKeep = new Set<string>()
    for (const key of SETTINGS_KEYS) {
      if (builtRecord[key] !== undefined) {
        keysToKeep.add(key)
      }
    }

    const patch: Record<string, unknown> = {}
    for (const key of Object.keys(rawSettings)) {
      if (
        (SETTINGS_KEYS as readonly string[]).includes(key) &&
        !keysToKeep.has(key)
      ) {
        patch[key] = null
      }
    }

    for (const key of keysToKeep) {
      const setting = builtRecord[key]
      if (!setting) {
        continue
      }
      patch[key] = Settings.wireSettingForSave(setting)
    }

    return patch
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function overlaySettingForSave(
  key: string,
  json: Record<string, unknown>,
  rawSettings: unknown
): Record<string, unknown> {
  if (key === 'sessionSharedAccess') {
    return overlaySessionSharedAccessSetting(json, rawSettings)
  }

  return overlayArrayActuals(key, json, rawSettings)
}

function overlaySessionSharedAccessSetting(
  json: Record<string, unknown>,
  _rawSettings: unknown
): Record<string, unknown> {
  const nextActual = json['actual']
  if (!isRecord(nextActual)) {
    return json
  }

  // Replace shared-access actual wholesale so merge-patch cannot resurrect
  // skipped sizes from stale server basePrices / beneficiaryPrices.
  const actual: Record<string, unknown> = {
    basePrices: nextActual['basePrices'] ?? [],
    beneficiaryPrices: nextActual['beneficiaryPrices'] ?? [],
    excludedCategoryIds: nextActual['excludedCategoryIds'] ?? [],
  }
  if (Object.prototype.hasOwnProperty.call(nextActual, 'maxBeneficiaries')) {
    actual['maxBeneficiaries'] = nextActual['maxBeneficiaries']
  }

  return { ...json, actual }
}

function overlayArrayActuals(
  key: string,
  json: Record<string, unknown>,
  rawSettings: unknown
): Record<string, unknown> {
  if (!isRecord(rawSettings)) {
    return json
  }

  const rawSetting = rawSettings[key]
  if (!isRecord(rawSetting)) {
    return json
  }

  const rawActual = rawSetting['actual']
  const nextActual = json['actual']
  if (!Array.isArray(nextActual) || !Array.isArray(rawActual)) {
    return json
  }

  const keyedOverlay = ARRAY_ACTUAL_OVERLAY_KEY[key]
  if (keyedOverlay) {
    const rawByKey = new Map<string, Record<string, unknown>>()
    for (const raw of rawActual) {
      if (isRecord(raw)) {
        rawByKey.set(keyedOverlay(raw), raw)
      }
    }

    return {
      ...json,
      actual: nextActual.map(item => {
        if (!isRecord(item)) {
          return item
        }
        const rawItem = rawByKey.get(keyedOverlay(item))
        return rawItem ? mergePatch(rawItem, item) : item
      }),
    }
  }

  return {
    ...json,
    actual: nextActual.map((item, index) => {
      const rawItem = rawActual[index]
      if (isRecord(item) && isRecord(rawItem)) {
        return mergePatch(rawItem, item)
      }
      return item
    }),
  }
}

const ARRAY_ACTUAL_OVERLAY_KEY: Record<
  string,
  (item: Record<string, unknown>) => string
> = {
  lockerBaseRates: item =>
    `${String(item['priceCategoryId'] ?? '')}|${String(item['currency'] ?? '')}`,
  serviceFee: item => String(item['currency'] ?? ''),
  depositionPrice: item => String(item['currency'] ?? ''),
  unlockPrices: item => {
    const price = item['price']
    const currency = isRecord(price) ? price['currency'] : undefined
    const type = String(item['type'] ?? '')
    const count = String(item['count'] ?? '')
    return `${type}|${count}|${String(currency ?? '')}`
  },
}

export class Rule<T> {
  startDate: Date
  endDate: Date
  startTime: Date
  endTime: Date
  weekdays?: Weekdays
  state: boolean
  payload?: T

  constructor(json: any) {
    this.startDate = new Date(json.startDate)
    this.endDate = new Date(json.endDate)
    this.startTime = new Date(json.startTime)
    this.endTime = new Date(json.endTime)
    this.weekdays = json.weekdays
    this.state = json.state
    this.payload = json.payload
    rememberPayload(this, unwrapLegacyExtras(json))
  }

  toJSON(): Record<string, unknown> {
    return wireEntity(this) as Record<string, unknown>
  }

  static getFactory<T>(): Factory<Rule<T>> {
    return new (class implements Factory<Rule<T>> {
      make(json: any): Rule<T> {
        return new Rule<T>(json)
      }

      getTableName(): string {
        throw new Error(
          'getTableName() should never be called on a Rule. Rules are not stored in IndexDB.'
        )
      }
    })()
  }
}
